import { ChevronDownIcon, ChevronRightIcon, SparklesIcon } from "@heroicons/react/24/outline";
import { type FC, useState } from "react";
import { Link } from "react-router-dom";

import { type AcceptedPromptSuggestion, useListAcceptedPromptSuggestions } from "@api/rest/types-and-hooks";
import { SuggestionPRLinks } from "@pages/Evaluations/SuggestionPRLinks";
import { acceptedWindowLabel, rateChange } from "@pages/Evaluations/evalResultsProduction";
import { suggestionEvaluationsPath } from "@pages/Evaluations/promptSuggestionFields";

const AcceptedRow: FC<{ suggestion: AcceptedPromptSuggestion; showOrg: boolean }> = ({ suggestion, showOrg }) => (
  <li className="flex flex-wrap items-start justify-between gap-4 px-4 py-3">
    <div className="flex min-w-0 flex-col gap-y-1">
      <Link
        to={suggestionEvaluationsPath(suggestion.prompt_key, suggestion.id)}
        className="text-sm font-medium text-text-primary hover:underline"
      >
        {suggestion.theme_title}
      </Link>
      <span className="text-xs text-text-secondary">
        {suggestion.prompt_name}
        {showOrg ? ` · ${suggestion.organization_name}` : ""}
        {suggestion.shipped_at ? ` · PR opened ${new Date(suggestion.shipped_at).toLocaleDateString()}` : ""}
      </span>
      {suggestion.pipelines_pr_url ? (
        <SuggestionPRLinks pipelinesUrl={suggestion.pipelines_pr_url} apiDjangoUrl={suggestion.api_django_pr_url} />
      ) : null}
      {/* Shipped in the same PRs, incorporated at Review draft. */}
      {suggestion.incorporated.length > 0 ? (
        <span className="text-xs text-text-tertiary">
          (Incorporated: {suggestion.incorporated.map((s) => `“${s.theme_title}”`).join(", ")})
        </span>
      ) : null}
    </div>
    <div className="flex flex-col items-end gap-y-1 text-xs">
      {suggestion.current_pct_fields_edited == null ? (
        <span className="text-text-secondary">No Eval Results numbers for this prompt yet.</span>
      ) : (
        <>
          <span className="text-text-primary">
            Edited {rateChange(suggestion.baseline_pct_fields_edited, suggestion.current_pct_fields_edited)} · Edit size{" "}
            {rateChange(suggestion.baseline_avg_edit_percentage, suggestion.current_avg_edit_percentage)}
          </span>
          <span className="text-text-tertiary">{acceptedWindowLabel(suggestion.shipped_at)}</span>
        </>
      )}
    </div>
  </li>
);

/** Rio's shipped suggestions and how their prompts are faring (PROC-266): one
 *  org's, or every org's. Before is the rate when Rio drafted it, now is Eval
 *  Results' last 90 days. A collapsed bar; nothing when there are none. */
export const AcceptedSuggestions: FC<{ orgId: number | null }> = ({ orgId }) => {
  const [open, setOpen] = useState(false);
  const { data } = useListAcceptedPromptSuggestions(orgId !== null ? { organization: orgId } : {});
  const suggestions = data?.status === 200 ? data.data.suggestions : [];
  if (suggestions.length === 0) return null;

  return (
    <section className="rounded-lg border border-blurple-200 bg-bg-surface">
      <button
        type="button"
        onClick={() => setOpen((wasOpen) => !wasOpen)}
        aria-expanded={open}
        className="flex w-full items-center gap-x-2 px-4 py-3 text-left"
      >
        <SparklesIcon className="size-4 text-blurple-600" aria-hidden="true" />
        <span className="text-sm font-semibold text-blurple-600">
          Rio&apos;s accepted suggestions ({suggestions.length})
        </span>
        <span className="text-xs text-text-secondary">How their prompts are faring since the PR opened</span>
        {open ? (
          <ChevronDownIcon className="ml-auto size-4 text-text-secondary" aria-hidden="true" />
        ) : (
          <ChevronRightIcon className="ml-auto size-4 text-text-secondary" aria-hidden="true" />
        )}
      </button>
      {open ? (
        <ul className="divide-y divide-border-subtle border-t border-border-subtle">
          {suggestions.map((suggestion) => (
            <AcceptedRow key={suggestion.id} suggestion={suggestion} showOrg={orgId === null} />
          ))}
        </ul>
      ) : null}
    </section>
  );
};
